/**
 * Operational Clarity Review — controlled instrument configuration v2.2.0
 * Controlled by: BOS Operational Clarity Review Copy Alignment Review v2.2
 *
 * @typedef {"direction"|"standards"|"ownership"|"authority"|"behaviour"} ClarityDimension
 * @typedef {1|2|3|4|5|"NA"|"NV"} AnswerValue
 * @typedef {"holding-up"|"mostly-working"|"breaking-down"|"getting-in-the-way"|"insufficient"} ClarityBand
 * @typedef {"low"|"some"|"material"|"high"|"unclassified"} FrictionBand
 * @typedef {"none"|"possible"|"strong"|"not-available"} MismatchLevel
 */

export const INSTRUMENT_VERSION = "2.2.0";

export const INTRO_EYEBROW = "Operational Clarity Review";
export const INTRO_TITLE = "Check one area of work that keeps causing friction.";

export const INTRO_PARAGRAPHS = [
  "Something is getting in the way of reliable execution.",
  "Choose one regular piece of work where something keeps going wrong, slowing down or coming back to a manager.",
  "The review asks 18 questions about what has actually happened during the past eight weeks. It looks at five things that people need in order to work consistently:",
  "Direction · Standards · Ownership · Authority · Behaviour",
  "It also checks how often routine work is creating repeated questions, checking or manager involvement.",
  "You will receive the full result on screen.",
];

export const INTRO_BOUNDARY =
  "This is a directional business diagnostic. It can show a pattern worth investigating, but it does not prove root cause or assess individual employees.";

export const RESPONDENT_INSTRUCTION_TITLE = "Answer about what actually happened";
export const RESPONDENT_INSTRUCTION =
  "Think about the past eight weeks, not how the work is supposed to run. Answer based on what usually happened when each situation came up. If it did not happen, choose This did not come up. If you do not see enough of the work to judge, choose I do not see enough of this to answer. If practice changes between leaders, teams or situations, answer based on the overall pattern - not the best example.";

export const RESPONSE_SCALE = [
  { value: 1, label: "Not once" },
  { value: 2, label: "In a few cases" },
  { value: 3, label: "About half the time" },
  { value: 4, label: "Most of the time" },
  { value: 5, label: "Nearly every time" },
];

export const UNAVAILABLE_OPTIONS = [
  { value: "NA", label: "This did not come up" },
  { value: "NV", label: "I do not see enough of this to answer" },
];

export const QUESTIONS = [
  { n: 1, id: "D1", kind: "clarity", dimension: "direction", polarity: "positive", text: "When priorities compete, people make the same call about what comes first." },
  { n: 2, id: "O1", kind: "clarity", dimension: "ownership", polarity: "positive", text: "When work moves from one person or team to another, it is clear who has the next step." },
  { n: 3, id: "F1", kind: "friction", dimension: null, polarity: "negative", text: "Managers are asked the same questions about routine work more than once." },
  { n: 4, id: "S1", kind: "clarity", dimension: "standards", polarity: "positive", text: "People doing the same type of work use the same basic standard for what ‘done properly’ looks like." },
  { n: 5, id: "A1", kind: "clarity", dimension: "authority", polarity: "positive", text: "People make routine decisions in their role without checking with a manager first." },
  { n: 6, id: "B1", kind: "clarity", dimension: "behaviour", polarity: "positive", text: "Leaders use practical examples to show what expected behaviour looks like in practice." },
  { n: 7, id: "D2", kind: "clarity", dimension: "direction", polarity: "positive", text: "When plans change or pressure rises, leaders keep giving the same direction about what matters most." },
  { n: 8, id: "O3", kind: "clarity", dimension: "ownership", polarity: "negative", text: "Work waits because each person thinks someone else is responsible." },
  { n: 9, id: "S2", kind: "clarity", dimension: "standards", polarity: "positive", text: "When a job does not fit the usual process, people know what must not be compromised." },
  { n: 10, id: "F2", kind: "friction", dimension: null, polarity: "positive", text: "Routine work gets done without repeated manager checking or reminders." },
  { n: 11, id: "A2", kind: "clarity", dimension: "authority", polarity: "positive", text: "When a situation falls outside the usual rules, people know whether to decide, ask for advice or escalate it." },
  { n: 12, id: "B3", kind: "clarity", dimension: "behaviour", polarity: "negative", text: "The behaviour leaders accept changes depending on the leader or the person involved." },
  { n: 13, id: "O2", kind: "clarity", dimension: "ownership", polarity: "positive", text: "When work is incomplete or disputed, leaders help resolve it without automatically taking over." },
  { n: 14, id: "D3", kind: "clarity", dimension: "direction", polarity: "negative", text: "Managers have to step in because people are working to different priorities." },
  { n: 15, id: "F3", kind: "friction", dimension: null, polarity: "negative", text: "Managers or experienced people step in to check, fix or finish work that is meant to run without them." },
  { n: 16, id: "A3", kind: "clarity", dimension: "authority", polarity: "negative", text: "Managers are asked to approve routine decisions that are meant to sit with the person doing the work." },
  { n: 17, id: "B2", kind: "clarity", dimension: "behaviour", polarity: "positive", text: "Under pressure, leaders behave in the same way they expect everyone else to behave." },
  { n: 18, id: "S3", kind: "clarity", dimension: "standards", polarity: "negative", text: "Work is checked or redone because people have different views of what is acceptable." },
];

export const WRITTEN_EXAMPLE = {
  label: "One recent example — optional",
  prompt:
    "Think of the last time the problem showed up. What was the work, where did it get stuck, and what did the manager have to do? Do not include employee names or sensitive information.",
  maxLength: 500,
};

export const BAND_LABELS = {
  "holding-up": "Holding up well",
  "mostly-working": "Mostly working",
  "breaking-down": "Breaking down",
  "getting-in-the-way": "Getting in the way",
  insufficient: "Not enough recent evidence for this area",
};

export const FRICTION_BAND_LABELS = {
  low: "Low",
  some: "Some",
  material: "Material",
  high: "High",
  unclassified: "Not enough recent evidence to classify day-to-day friction",
};

export const FRICTION_NARRATIVES = {
  low: "Routine work mostly runs without repeated questions, checking or manager intervention.",
  some: "Some repeated questions, checking or manager help is occurring, but it appears occasional or local.",
  material: "Repeated questions, escalation, checking or manager involvement are affecting how the work runs.",
  high: "Routine work is relying heavily on managers or experienced people to keep it moving, check it or correct it.",
  unclassified: "There is not enough recent evidence to classify day-to-day friction.",
};

export const DIMENSIONS = [
  {
    key: "direction",
    name: "Direction",
    itemIds: ["D1", "D2", "D3"],
    narratives: {
      "holding-up": "People usually know what matters most, and leaders keep the direction steady when pressure rises.",
      "mostly-working": "Direction is usually clear, but it changes in some situations or between leaders.",
      "breaking-down": "Priorities are not clear enough to guide the work consistently. Managers still have to settle what comes first.",
      "getting-in-the-way": "People are regularly pulled in different directions. Managers have to keep deciding what comes first during the work.",
    },
    question: "When work competes, have we agreed what wins?",
    action: "Agree the result this area must deliver and what takes priority when normal demands compete.",
  },
  {
    key: "standards",
    name: "Standards",
    itemIds: ["S1", "S2", "S3"],
    narratives: {
      "holding-up": "People usually share the same view of what good work looks like, including when the usual process does not fit.",
      "mostly-working": "The standard is mostly clear, but exceptions or different leader expectations still create variation.",
      "breaking-down": "People are using different views of what is acceptable, which is leading to extra checking or rework.",
      "getting-in-the-way": "The standard is not dependable enough to guide the work. People cannot be confident what will be accepted until someone checks it.",
    },
    question: "Have we agreed what ‘done properly’ means and what must not be compromised?",
    action: "Set the basic standard, what must not be compromised and where people can use judgement.",
  },
  {
    key: "ownership",
    name: "Ownership",
    itemIds: ["O1", "O2", "O3"],
    narratives: {
      "holding-up": "People usually know who owns the next step and who stays responsible when work becomes difficult.",
      "mostly-working": "Ownership is mostly clear, but some handovers or disputed work still stall.",
      "breaking-down": "Work is losing momentum at handovers or when responsibility is disputed.",
      "getting-in-the-way": "Responsibility is regularly unclear or pushed upward. Work waits until a manager steps in.",
    },
    question: "Who owns the final result, and what marks a completed handover?",
    action: "Name the owner of the final result and make the start, finish and handover points clear.",
  },
  {
    key: "authority",
    name: "Authority",
    itemIds: ["A1", "A2", "A3"],
    narratives: {
      "holding-up": "People usually know what they can decide, when to ask for advice and when to escalate.",
      "mostly-working": "Routine decisions usually stay in the role, but exceptions or some leaders still pull decisions upward.",
      "breaking-down": "People are checking or escalating decisions that should be handled closer to the work.",
      "getting-in-the-way": "Decision boundaries are not dependable. Asking a manager is the safer option, even for routine work.",
    },
    question: "Do people know what they can decide, when to ask and when they must escalate?",
    action: "List the decisions people can make, when they should ask for advice and when they must escalate.",
  },
  {
    key: "behaviour",
    name: "Behaviour",
    itemIds: ["B1", "B2", "B3"],
    narratives: {
      "holding-up": "Leaders usually show, follow and reinforce the behaviour they expect from others.",
      "mostly-working": "Expected behaviour is mostly clear, but pressure or different leaders still change what is accepted.",
      "breaking-down": "People are receiving mixed signals about the behaviour that matters and what leaders will accept.",
      "getting-in-the-way": "What leaders say and what they reinforce do not line up consistently enough for people to know what will actually be accepted.",
    },
    question: "Are leaders showing and reinforcing the same behaviour they ask of everyone else?",
    action: "Turn words such as accountability, initiative or teamwork into actions people can see, then apply the same standard to leaders.",
  },
];

export const MISMATCH_INTRO =
  "Some of your answers point in different directions. That can happen when the work changes between leaders, teams or situations. It can also happen when two questions were understood differently. Check one recent example before drawing a conclusion.";

export const MISMATCH_PAIRS = [
  {
    area: "direction",
    positiveId: "D1",
    negativeId: "D3",
    message: "You said people usually make the same call about what comes first, but managers still step in because people are working to different priorities. Check whether priorities change between leaders, teams or periods of pressure.",
  },
  {
    area: "ownership",
    positiveId: "O1",
    negativeId: "O3",
    message: "You said handovers are usually clear, but work still waits because people think someone else is responsible. Check the points where work moves between people or teams.",
  },
  {
    area: "standards",
    positiveId: "S1",
    negativeId: "S3",
    message: "You said people use the same standard, but work is still checked or redone because people have different views of what is acceptable. Check whether the standard changes by leader, job or situation.",
  },
  {
    area: "authority",
    positiveId: "A1",
    negativeId: "A3",
    message: "You said people usually make routine decisions without checking first, but managers are still being asked to approve decisions that should sit with the person doing the work. Check whether the decision boundary changes depending on the leader, risk or situation.",
  },
  {
    area: "behaviour",
    positiveId: "B1",
    negativeId: "B3",
    message: "You said leaders show what is expected, but what they accept still changes depending on the leader or person involved. Check whether leaders are reinforcing the same behaviour consistently.",
  },
  {
    area: "friction",
    positiveId: "F2",
    negativeId: "F3",
    message: "You said routine work usually runs without repeated checking, but managers or experienced people still step in to check, fix or finish it. Check whether good results are being held together by extra management effort.",
  },
];

export const FRICTION_CONTROL_ACTION =
  "For two weeks, record each time a manager has to clarify, approve, check, fix or finish routine work. Note the repeated reason.";

export const ALTERNATIVE_CONSTRAINT_ACTION =
  "Check workload, capability, process, technology, resources, incentives and outside constraints before changing how the work is being led.";

export const CATEGORIES = {
  "not-enough-information": {
    label: "There is not enough recent evidence to call this clearly.",
    summary: "You did not see enough of this work during the past eight weeks for the review to give a firm result. The situation may not have come up often enough, you may not see enough of the work, or the area being reviewed may be too broad.",
  },
  "several-breakdowns": {
    label: "The way the work runs is breaking down in several places.",
    summary: "Several of the basics are not holding up, and day-to-day friction is showing up in the work. This is unlikely to be fixed by simply asking people for more accountability. Direction, standards, ownership, authority or leadership behaviour need a closer look.",
  },
  "focused-breakdown": {
    label: "One or two weak spots are creating avoidable friction.",
    summary: "Most of the basics appear to be working, but one or two areas are not holding up consistently. That is showing up in extra questions, escalation, checking or manager involvement.",
  },
  "another-cause": {
    label: "The friction is real, but lack of clarity may not be the main cause.",
    summary: "The five Operational Clarity areas mostly appear to be holding up, but the work is still producing repeated questions, checking, escalation or manager involvement. Workload, capability, process design, technology, resources, incentives or the operating environment may be more important.",
  },
  "gaps-low-friction": {
    label: "Some basics need tightening, but they are not causing much friction right now.",
    summary: "One or more clarity areas appear weak, but you reported little repeated escalation, checking or manager involvement. The gaps may be local, the work may not be testing them yet, or managers may be compensating before problems become visible.",
  },
  "mostly-clear": {
    label: "This area is mostly running clearly.",
    summary: "The five basics mostly appear to be in place, and reported day-to-day friction is low to moderate. If a recurring problem remains, it may be local or driven by something outside Operational Clarity.",
  },
};

export const PRIMARY_SIGNAL_NONE = "No single area stands out on its own";
export const STRONGEST_TIE = "Several areas appear similarly established";

export const OTHER_POSSIBLE_CAUSES =
  "Lack of Operational Clarity is not the only possible cause. Capability, workload, resources, process design, technology, structure, incentives, regulation or the operating environment may explain part or all of the problem.";

export const LIMITATIONS = [
  "This result comes from one leader's view of one area of work. It does not show what individual employees understand, prove the root cause or assess anyone's performance. Use it to decide what deserves a closer look.",
  "The Operational Clarity Review is a directional business diagnostic. It is not a psychometric assessment, organisational audit or validated measure of organisational effectiveness.",
];

export const CTA_PRIMARY = {
  label: "DISCUSS THE RECURRING PROBLEM",
  to: "/contact",
  supporting: "If this result reflects something you keep seeing, talk through what is happening, where managers are being pulled in and whether BOS Reveal would help.",
};
export const CTA_SECONDARY = { label: "REVIEW ANOTHER AREA" };
export const CTA_TEXT_LINK = { label: "See how BOS works →", to: "/how-bos-works" };
